import { JsonObject, JsonProperty } from "typescript-json-serializer";
import {SpatialReferenceConfiguration} from "./spatial-reference-configuration";

@JsonObject()
export class GeometryConfiguration {
    /**
     * Indicates if the geometry has M values.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Geometry.html#hasM)
     */
    @JsonProperty()
    declare hasM?: boolean;

    /**
     * Indicates if the geometry has z-values (elevation).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Geometry.html#hasZ)
     */
    @JsonProperty()
    declare hasZ?: boolean;

    /**
     * The spatial reference of the geometry.
     *
     * @default SpatialReference.WGS84 // wkid: 4326
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Geometry.html#spatialReference)
     */
    @JsonProperty({type: SpatialReferenceConfiguration})
    declare spatialReference?: SpatialReferenceConfiguration;

    /**
     * The geometry type.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Geometry.html#type)
     */
    @JsonProperty()
    declare type: "point" | "multipoint" | "polyline" | "polygon" | "extent" | "mesh";

    constructor() {}
}

@JsonObject()
export class ExtentConfiguration extends GeometryConfiguration {
    /**
     * The maximum possible `m` value in an extent envelope.
     *
     * @default undefined
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Extent.html#mmax)
     */
    @JsonProperty()
    declare mmax?: number;

    /**
     * The minimum possible `m` value of an extent envelope.
     *
     * @default undefined
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Extent.html#mmin)
     */
    @JsonProperty()
    declare mmin?: number;

    /**
     * The maximum X-coordinate of an extent envelope.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Extent.html#xmax)
     */
    @JsonProperty()
    declare xmax?: number;

    /**
     * The minimum X-coordinate of an extent envelope.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Extent.html#xmin)
     */
    @JsonProperty()
    declare xmin?: number;

    /**
     * The maximum Y-coordinate of an extent envelope.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Extent.html#ymax)
     */
    @JsonProperty()
    declare ymax?: number;

    /**
     * The minimum Y-coordinate of an extent envelope.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Extent.html#ymin)
     */
    @JsonProperty()
    declare ymin?: number;

    /**
     * The maximum possible `z`, or elevation, value in an extent envelope.
     *
     * @default undefined
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Extent.html#zmax)
     */
    @JsonProperty()
    declare zmax?: number;

    /**
     * The minimum possible `z`, or elevation, value of an extent envelope.
     *
     * @default undefined
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Extent.html#zmin)
     */
    @JsonProperty()
    declare zmin?: number;

    constructor() {
        super();
    }
}

@JsonObject()
export class PointConfiguration extends GeometryConfiguration {
    /**
     * The latitude of the point.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Point.html#latitude)
     */
    @JsonProperty()
    declare latitude?: number;
    /**
     * The longitude of the point.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Point.html#longitude)
     */
    @JsonProperty()
    declare longitude?: number;
    /**
     * The m-coordinate of the point in map units.
     *
     * @default undefined
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Point.html#m)
     */
    @JsonProperty()
    declare m?: number;
    /**
     * The x-coordinate (easting) of the point in map units.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Point.html#x)
     */
    @JsonProperty()
    declare x?: number;
    /**
     * The y-coordinate (northing) of the point in map units.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Point.html#y)
     */
    @JsonProperty()
    declare y?: number;
    /**
     * The z-coordinate (or elevation) of the point in map units.
     *
     * @default undefined
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Point.html#z)
     */
    @JsonProperty()
    declare z?: number;

    constructor() {
        super();
    }
}
